<template>
  <div>
    <div class="bg-background-neutral-secondary space-y-1 rounded-2xl p-1">
      <FlightDelayInfoButton
        v-if="
          separateProducts?.some(
            product => product?.type === 'products.flight-delay-insurance',
          )
        "
        @on-click="bottomSheet.show(FLIGHT_DELAY_INFO_BOTTOM_SHEET)"
      />
      <section class="space-y-4 p-3">
        <ProductPriceInfo
          v-if="!isNil(ticketPrice)"
          :title="$t('general.price.ticket')"
          :price="ticketPrice"
        />
        <ProductPriceInfo
          v-if="!isNil(baggagePrice)"
          :title="$t('ancillaries.baggages.title')"
          :price="baggagePrice"
        />
        <ProductPriceInfo
          v-if="!isNil(seatMapPrice)"
          :title="$t('ancillaries.seatMap.title')"
          :price="seatMapPrice"
        />
        <ProductPriceInfo
          v-if="!isNil(roamingPrice)"
          :title="$t('roaming.roaming')"
          :price="roamingPrice"
        />
        <ProductPriceInfo
          v-if="!isNil(medicalInsurancePrice)"
          :title="$t('ancillaries.medicalInsurance.title')"
          :price="medicalInsurancePrice"
        />
        <!-- Default package has title '' -->
        <ProductPriceInfo
          v-if="productsPackage && productsPackage.title"
          :title="t('booking.package', { title: productsPackage.title })"
          :price="productsPackage.price"
        />
        <ProductPriceInfo
          v-if="discountValue"
          :title="t('promocode.pricingTitle')"
          :price="discountValue"
        />
        <ProductPriceInfo
          v-for="(product, idx) in separateProducts"
          :key="idx"
          :title="product.title"
          :price="product.price"
        />
        <PriceModifierInfo
          v-if="!isNil(promocodePrice)"
          :title="$t('promocode.pricingTitle')"
          :price="promocodePrice"
        />
        <div v-if="!isNil(loyaltyCashbackAmount)" class="flex justify-between">
          <span class="text-text-neutral-tertiary w-3/5 text-base">{{
            $t('loyalty.pricing-title')
          }}</span>
          <span
            class="text-text-negative-primary shrink-0 text-base font-semibold"
          >
            {{ prettyPrice(toString(loyaltyCashbackAmount)) }} ₸
          </span>
        </div>
        <p class="flex justify-between">
          <span class="text-text-neutral-tertiary text-lg font-medium">{{
            $t('general.price.total')
          }}</span>
          <span
            v-price="totalPrice"
            class="text-text-neutral-primary text-lg font-semibold"
          ></span>
        </p>
        <div v-if="amount > 0" class="flex items-center justify-between">
          <div>
            <p class="text-text-neutral-tertiary text-sm font-medium">
              {{ $t('main.loyalty.amount') }}
            </p>
            <p class="text-text-neutral-quaternary text-xs">
              {{ cashbackPercent }}%
            </p>
          </div>
          <p v-price="amount" class="freedom-loyalty-text font-semibold"></p>
        </div>
      </section>
    </div>
    <FlightDelayInfoBottomSheet :name="FLIGHT_DELAY_INFO_BOTTOM_SHEET" />
  </div>
</template>

<script setup lang="ts">
  import { toRef } from 'vue';
  import { isNil, toString } from 'lodash-es';
  import { freedomCashbackModel } from 'freedom-shared';
  import { useI18n } from 'vue-i18n';
  import { controllerModel } from 'shared';
  import { prettyPrice } from '@aviata/text-fns';

  import { api } from '@/shared/api';

  import ProductPriceInfo from './ProductPriceInfo.vue';
  import PriceModifierInfo from './PriceModifierInfo.vue';
  import FlightDelayInfoButton from './FlightDelayInfoButton.vue';
  import FlightDelayInfoBottomSheet from './FlightDelayInfoBottomSheet.vue';

  const FLIGHT_DELAY_INFO_BOTTOM_SHEET = 'flight-delay-info-bottom-sheet';

  type Price = number | string;

  type ProductInfo = {
    title: string;
    price?: Price;
  };

  const props = defineProps<{
    sourcePage?: string;
    billId: string;
    ticketPrice: Price | null;
    baggagePrice: Price | null;
    productsPackage: ProductInfo | null;
    separateProducts: ProductInfo[] | null;
    seatMapPrice: Price | null;
    promocodePrice?: Price | null;
    loyaltyCashbackAmount?: Price | null;
    totalPrice: Price;
    roamingPrice: Price | null;
    medicalInsurancePrice: Price | null;
    discountValue: Price | null;
  }>();

  const totalPrice = toRef(props, 'totalPrice');

  const { amount, cashbackPercent } = freedomCashbackModel.useFreedomCashback({
    pfm: api,
    offerPrice: totalPrice,
    billId: props.billId,
  });

  const { t } = useI18n();
  const bottomSheet = controllerModel.useBottomSheet();
</script>
